import React, { useEffect, useState } from "react";
import { useUserContext } from "../../utils/UserContext";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { formatDate, formatNumero } from "../../utils/Utilitats";
import FitxaRecursos from "./FitxaRecursos";
import { ChipEstatEina, esEinaActiva, idEina, ubicacioActual } from "./Seleccionareina";
import { Camp, CampImatge, ChipActiu, InterruptorActiu, Valor, cridaRecurs, useImatgeRecurs } from "./RecursosComuns";

const ESTATS_PER_DEFECTE = ["OPERATIVA", "AVARIADA", "REPARACIO", "BAIXA", "PERDUDA"];

const BUIT = {
  Nom: "",
  Marca: "",
  Model: "",
  NumeroSerie: "",
  DataCompra: "",
  PreuCompra: "",
  Estat: "OPERATIVA",
  Activa: true,
  Observacions: "",
};

function formDesDeRegistre(e) {
  const form = { ...BUIT };
  Object.keys(BUIT).forEach((clau) => {
    if (clau === "Activa") return;
    form[clau] = e[clau] === null || e[clau] === undefined ? "" : String(e[clau]);
  });
  form.Activa = esEinaActiva(e);
  return form;
}

function Detallseina({ mode, record, llista, meta, onNavigate, onCancel, onCanviarMode, onSave, onEstatCanviat }) {
  const { empresaSeleccionada } = useUserContext();
  const readOnly = mode === "consultar" || mode === "desactivar" || mode === "reactivar";
  const [form, setForm] = useState(BUIT);
  const imatge = useImatgeRecurs();
  const { reinicia } = imatge;

  useEffect(() => {
    if (record && mode !== "nou") {
      setForm(formDesDeRegistre(record));
      reinicia(record.Imatge);
    } else {
      setForm({ ...BUIT });
      reinicia("");
    }
  }, [record, mode, reinicia]);

  const canvia = (clau) => (e) => setForm((prev) => ({ ...prev, [clau]: e.target.value }));
  const estatsOptions = meta?.estats?.length ? meta.estats : ESTATS_PER_DEFECTE;

  const validar = () => {
    const errors = [];
    if (!form.Nom.trim()) errors.push("El camp Nom és obligatori.");
    if (form.PreuCompra !== "" && !/^\d+([.,]\d{1,2})?$/.test(String(form.PreuCompra).trim())) {
      errors.push("El Preu de compra ha de ser un import positiu amb un màxim de 2 decimals.");
    }
    return errors;
  };

  const desar = async () => {
    const errors = validar();
    if (errors.length > 0) {
      alert(`Errors trobats:\n${errors.join("\n")}`);
      return;
    }

    let nomImatge;
    try {
      nomImatge = await imatge.desa();
    } catch (error) {
      alert(`Error en pujar la imatge: ${error.message}`);
      return;
    }

    const payload = {
      ...form,
      idEmpresa: empresaSeleccionada?.IdEmpresa || null,
      Nom: form.Nom.trim(),
      Activa: form.Activa ? 1 : 0,
      Imatge: nomImatge,
    };
    if (mode === "modificar") payload.idEines = record.idEines;

    const resposta = await cridaRecurs(mode === "nou" ? "NovaEina.php" : "ModificarEina.php", payload);
    if (resposta.ok) {
      // La ubicació actual no ve del formulari: es conserva la que ja tenia el registre.
      onSave?.({
        ...resposta.data.eina,
        UbicacioActualNom: record?.UbicacioActualNom ?? null,
        UbicacioActualTipus: record?.UbicacioActualTipus ?? null,
      });
    } else if (!resposta.silenci) {
      alert(`Error en desar l'eina: ${resposta.message}`);
    }
  };

  const text = (clau, maxLength, extra = {}) =>
    readOnly ? (
      <Valor>{form[clau]}</Valor>
    ) : (
      <input type="text" className="dv2-input" value={form[clau]} maxLength={maxLength} onChange={canvia(clau)} {...extra} />
    );

  const nomUbicacio = record ? ubicacioActual(record) : "";
  const tipusUbicacio = record?.UbicacioActualTipus
    ? mapToPresentable(record.UbicacioActualTipus, mapejos.tipusUbicacio)
    : "";

  return (
    <FitxaRecursos
      mode={mode}
      record={record}
      llista={llista}
      idDe={idEina}
      onNavigate={onNavigate}
      onCancel={onCancel}
      onCanviarMode={onCanviarMode}
      onDesar={desar}
      onEstatCanviat={onEstatCanviat}
      estat={{ endpoint: "ActivarEina.php", idCamp: "idEines", campActiu: "Activa" }}
      esActiu={esEinaActiva}
      icona="🛠️"
      nomSingular="eina"
      nomArticle="l'eina"
      femeni
      subtitolNou="Una eina és un actiu reutilitzable de l'empresa, no depèn de cap treball concret."
      subtitolRegistre={form.Nom || "Dades de l'eina"}
      pestanyaAjuda="eines"
    >
      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">Identificació</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Nom" required readOnly={readOnly} mida="llarg">
            {text("Nom", 100, { autoFocus: mode === "nou" })}
          </Camp>
          <Camp label="Marca" readOnly={readOnly} mida="mig">
            {text("Marca", 50)}
          </Camp>
          <Camp label="Model" readOnly={readOnly} mida="llarg">
            {text("Model", 100)}
          </Camp>
          <Camp label="Número de sèrie" readOnly={readOnly} mida="llarg">
            {text("NumeroSerie", 100)}
          </Camp>
          <Camp label="Imatge" readOnly={readOnly} mida="ample">
            <CampImatge imatge={imatge} readOnly={readOnly} alt={`Imatge de l'eina ${form.Nom}`} icona="🛠️" />
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">2</span>
          <h2 className="dv2-card-title">Compra</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Data de compra" readOnly={readOnly}>
            {readOnly ? (
              <Valor>{form.DataCompra ? formatDate(form.DataCompra) : ""}</Valor>
            ) : (
              <input type="date" className="dv2-input" value={form.DataCompra} onChange={canvia("DataCompra")} />
            )}
          </Camp>
          <Camp label="Preu de compra (€)" readOnly={readOnly}>
            {readOnly ? (
              <Valor>{form.PreuCompra === "" ? "" : `${formatNumero(form.PreuCompra)} €`}</Valor>
            ) : (
              <input type="number" min="0" step="0.01" className="dv2-input" value={form.PreuCompra} onChange={canvia("PreuCompra")} />
            )}
          </Camp>
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">3</span>
          <h2 className="dv2-card-title">Estat</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Estat" required readOnly={readOnly} mida="mig">
            {readOnly ? (
              <ChipEstatEina estat={form.Estat} />
            ) : (
              <select className="dv2-select" value={form.Estat} onChange={canvia("Estat")}>
                {estatsOptions.map((s) => (
                  <option key={s} value={s}>
                    {mapToPresentable(s, mapejos.estatEina)}
                  </option>
                ))}
              </select>
            )}
          </Camp>
          <Camp label="Activa" readOnly={readOnly} mida="curt">
            {readOnly ? (
              <ChipActiu actiu={form.Activa} femeni />
            ) : (
              <InterruptorActiu valor={form.Activa} onChange={(v) => setForm((prev) => ({ ...prev, Activa: v }))} />
            )}
          </Camp>
          {mode !== "nou" && (
            <Camp label="Ubicació actual" readOnly mida="llarg">
              <Valor>{nomUbicacio ? [tipusUbicacio, nomUbicacio].filter(Boolean).join(" · ") : "Sense moviments"}</Valor>
            </Camp>
          )}
        </div>
        {mode !== "nou" && (
          <p className="dv2-hint">La ubicació és informativa: es dedueix de l'últim moviment de l'eina i no es desa a la fitxa.</p>
        )}
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">4</span>
          <h2 className="dv2-card-title">Altres</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <Camp label="Observacions" readOnly={readOnly} mida="ample">
            {readOnly ? (
              <Valor>{form.Observacions}</Valor>
            ) : (
              <textarea className="dv2-textarea" value={form.Observacions} maxLength={255} onChange={canvia("Observacions")} />
            )}
          </Camp>
        </div>
      </section>
    </FitxaRecursos>
  );
}

export default Detallseina;
